import { useLazyQuery } from '@apollo/client';
import PropTypes from 'prop-types';
import Cookies from 'js-cookie';
import React, {
  useEffect,
  useMemo,
  useReducer,
  useState,
  useContext
} from 'react';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import { grey20 } from 'Styles/colors';
import { ApplicationContext } from 'shop/context/ApplicationContext';
import { REBUY_CART_UPSELL_ID } from 'shop/lib/util/const';
import hive from '@bombas/hive';
import { RebuyUpsellWithData } from '../../../RebuyUpsell/RebuyUpsell';
import { PRODUCT_VARIANT_PIM_DATA } from '../../Cart.gql';

import { convertGlobalIdToRawId } from '../../../../services/storefront/cart';
import { vercel_base_url } from '../../../../util/config';
import { BUILD } from '../../../../util/const';
import { beginLoopCheckout, isLoopOnStore } from '../../../../util/loopReturns';
import { track } from '../../../../util/optimizely';
import { trackEvent } from '../../../../util/track';
import { getProductVariantUrl } from '../../../../util/util';
import { useIdentity } from '../../../Authentication/Authentication.hooks';
import DialogHeader from '../../../DialogHeader/DialogHeader';
import GiftMessageDialog from '../../../GiftMessageDialog/GiftMessageDialog';
import GiftOptionsDialog from '../../../GiftOptionsDialog/GiftOptionsDialog';
import ButtonIcon from '../../../Icons/ButtonIcon';
import ShopButtons from '../../../ShopButtons/ShopButtons';
import CartPropTypes from '../../Cart.propTypes';
import {
  initialState,
  UNDO_HIDE,
  UNDO_SHOW,
  undoStateReducer
} from '../../Cart.reducer';
import {
  calcLineItemDiscountedPriceAmount,
  calcTotalApplicableToFreeShippingThreshold,
  calcTotalCodeDiscountAmount,
  DIALOGS,
  fetchDiscountObject,
  formatPimData,
  getVariantCount,
  patchCartUrl,
  addQbsMessageToLineItems
} from '../../Cart.utils';
import { getCurrencySymbol } from '../../../../util/localize';

import * as Styled from './CartDialog.styled';

const TRANSITION_DURATION = 300;
const { GIFT_OPTIONS, GIFT_MESSAGE } = DIALOGS;

/**
 * Renders a cart within a drawer.
 */
const CartDialog = React.forwardRef(
  (
    {
      cart,
      isOpen,
      onAddToCart,
      onRequestClose,
      onQuantityUpdate,
      freeShippingThreshold,
      freeShippingMethod,
      upsellCollectionId,
      upsellTitle,
      upsellSubtitle,
      shippingPolicy,
      isAutoApplied,
      codeDiscountPercentage,
      siteConfig,
      setViewCouponDialogDisplay,
      ...props
    },
    ref
  ) => {
    const [undoState, undoStateDispatch] = useReducer(
      undoStateReducer,
      initialState
    );
    const { qbsDiscountData } = useContext(ApplicationContext);

    const isCartEmpty =
      !undoState.show && (cart?.lines?.edges.length || 0) <= 0;

    const discountObject = fetchDiscountObject(cart);

    const usableNetEnabled = Cookies.get('UsableNetAssistive');

    // fetches PIM Data for the variant upon undoing a variant removal from cart
    const [getProductVariantPimData, { error }] = useLazyQuery(
      PRODUCT_VARIANT_PIM_DATA,
      {
        onError: (err) => {
          window.DD_RUM?.onReady(() => {
            if (err.networkError) {
              window.DD_RUM?.addError(
                (new Error(err.networkError),
                {
                  location: 'cart',
                  type: 'network',
                  request: 'getProductVariantPIMData'
                })
              );
            }

            err.graphQLErrors.forEach((e) => {
              window.DD_RUM?.addError(new Error(e), {
                location: 'cart',
                type: 'graphQL',
                request: 'getProductVariantPIMData'
              });
            });
          });
        }
      }
    );

    const codeDiscountAmount = useMemo(
      () => calcTotalCodeDiscountAmount(cart?.discountAllocations),
      [cart]
    );

    const cartItemCount = useMemo(
      () => getVariantCount(cart?.lines?.edges),
      [cart]
    );

    const [dialogToDisplay, setDialogToDisplay] = useState(null);
    // Always Turn on Prioritized Gifting for Holiday
    // const [isGiftingPrioritized, setIsGiftingPrioritized] = useState(true);
    const isGiftingPrioritized = false;

    const { user } = useIdentity();

    const country = user?.locale?.country;

    const base_url = BUILD === 'shopify' ? vercel_base_url : '';

    // Temp turns off decisioning via Optimizely to preserve impressions
    // useEffect(() => {
    //   async function setPrioritizedGifting() {
    //     const prioritizedGiftingDecision = await decide('prioritized_gifting_v1');
    //     const isPrioritizedGiftingEnabled =
    //       prioritizedGiftingDecision?.variationKey === 'test';

    //     if (isPrioritizedGiftingEnabled)
    //       setIsGiftingPrioritized(isPrioritizedGiftingEnabled);
    //   }

    //   setPrioritizedGifting();
    // });

    useEffect(() => {
      let clearUndoState;
      if (undoState.show) {
        clearUndoState =
          !usableNetEnabled &&
          setTimeout(() => undoStateDispatch({ type: UNDO_HIDE }), 4000);
      }

      return () => {
        clearTimeout(clearUndoState);
      };
    }, [undoState, usableNetEnabled]);

    const cartItems = addQbsMessageToLineItems(
      cart?.lines?.edges,
      qbsDiscountData
    );

    return (
      <>
        <Styled.CartDialog
          data-label="miniCart"
          isOpen={isOpen}
          isEmpty={isCartEmpty}
          hasBackdrop={false}
          onRequestClose={onRequestClose}
          data-track-region="minicart"
          {...props}
        >
          <DialogHeader
            title="Shopping Bag"
            leftComponent={
              <Styled.CartPageLink
                href={`${base_url}/cart`}
                as={BUILD === 'shopify' && 'a'}
              >
                <Styled.CartNotificationIcon
                  name="bag"
                  count={cartItemCount}
                  role="presentation"
                  tabIndex="-1"
                />
              </Styled.CartPageLink>
            }
            rightComponent={
              <ButtonIcon
                variant="defaultLight"
                name="close"
                onClick={onRequestClose}
                data-testid="CartDialog__close-button"
                data-track-label="close"
                aria-label="close mini bag"
                data-label="miniCart_close"
              />
            }
            withDivider={false}
          />
          <Styled.ScrollContainer ref={ref}>
            {(country === 'US' || country === 'CA') && (
              <Styled.FreeShipping
                shippingPolicy={shippingPolicy}
                threshold={freeShippingThreshold}
                method={freeShippingMethod}
                total={calcTotalApplicableToFreeShippingThreshold(
                  cart?.lines?.edges,
                  codeDiscountAmount
                )}
                backgroundColor={grey20}
                currencyCode={cart?.cost?.subtotalAmount?.currencyCode}
                currencySymbol={getCurrencySymbol(country)}
              />
            )}

            {isCartEmpty && (
              <>
                <Styled.EmptyCartTitle>
                  Your bag is empty.
                </Styled.EmptyCartTitle>
                <ShopButtons
                  variant="secondary"
                  size="large"
                  hasLargeButtonGap
                />
              </>
            )}

            {!isCartEmpty && (
              <>
                <Styled.CartLineItems>
                  {isGiftingPrioritized && (
                    <Styled.GiftMessageCardAdd
                      withImage={false}
                      title="Is this a gift?"
                      description="We'll help make it extra special."
                      onClick={() => {
                        setDialogToDisplay(GIFT_OPTIONS);
                        trackEvent({ event: 'gift_options_prompt' });
                      }}
                    />
                  )}
                  {cart.note && (
                    <Styled.GiftMessageCardEdit
                      note={cart.note}
                      onEditClick={() => setDialogToDisplay(GIFT_MESSAGE)}
                    />
                  )}
                  <CSSTransition
                    in={undoState.show}
                    unmountOnExit
                    classNames="fade"
                    timeout={TRANSITION_DURATION}
                  >
                    <Styled.ConfirmationCardWrapper
                      transitionDuration={TRANSITION_DURATION}
                    >
                      <Styled.ConfirmationCard
                        title={undoState.product?.title}
                        onUndo={async () => {
                          await onAddToCart({
                            variantId: undoState.product?.variantId,
                            quantity: undoState.product?.quantity,
                            sku: undoState.product?.sku,
                            pdpLink: undoState.product?.pdpLink,
                            pimData: undoState.product?.pimData,
                            productId: undoState.product?.productId
                          });
                          undoStateDispatch({ type: UNDO_HIDE });
                        }}
                      />
                    </Styled.ConfirmationCardWrapper>
                  </CSSTransition>
                  <TransitionGroup>
                    {cartItems.map((lineItem) => {
                      const isGiftCard =
                        lineItem.node.merchandise.product?.handle.includes(
                          'gift-card'
                        );

                      const automaticDiscountAllocationNode =
                        lineItem?.node.discountAllocations.find(
                          (allocation) =>
                            allocation?.__typename ===
                            'CartAutomaticDiscountAllocation'
                        );

                      const lineItemDiscountAmount = parseFloat(
                        automaticDiscountAllocationNode?.discountedAmount.amount
                      );

                      const discountTitle =
                        automaticDiscountAllocationNode?.title;

                      const lineItemAmount =
                        lineItem.node.merchandise.price?.amount;

                      const discountedPrice = calcLineItemDiscountedPriceAmount(
                        Number(lineItemAmount),
                        lineItemDiscountAmount,
                        lineItem.node.quantity
                      );

                      return (
                        <CSSTransition
                          key={lineItem.node.id}
                          timeout={TRANSITION_DURATION}
                          classNames="fade"
                        >
                          <Styled.ProductVariantWrapper
                            transitionDuration={TRANSITION_DURATION}
                          >
                            <Styled.ProductVariant
                              title={lineItem.node.merchandise.product.title}
                              subtitle={
                                !isGiftCard
                                  ? lineItem.node.merchandise.title
                                  : null
                              }
                              currencyCode={
                                lineItem.node.merchandise.price.currencyCode
                              }
                              discountAmount={lineItemDiscountAmount}
                              discountedPrice={discountedPrice}
                              discountTitle={discountTitle}
                              lineItemAmount={lineItemAmount}
                              originalPrice={
                                lineItem.node.merchandise.compareAtPrice?.amount
                              }
                              image={lineItem.node.merchandise.image.url}
                              quantity={lineItem.node.quantity}
                              lineItemId={lineItem.node.id}
                              variantId={convertGlobalIdToRawId(
                                lineItem.node.merchandise.id
                              )}
                              productVariantUrl={getProductVariantUrl(
                                lineItem.node.merchandise
                              )}
                              onQuantityUpdate={(newQty) => {
                                onQuantityUpdate(
                                  lineItem.node.id,
                                  newQty,
                                  lineItem.node.merchandise.id,
                                  lineItem.node.quantity
                                ).then(async () => {
                                  if (newQty === 0) {
                                    const variantId = convertGlobalIdToRawId(
                                      lineItem.node.merchandise.id
                                    );

                                    track('Items_removed_from_cart_dialog');
                                    const productId = convertGlobalIdToRawId(
                                      lineItem.node.merchandise.product.id
                                    );

                                    const rawPimData =
                                      await getProductVariantPimData({
                                        variables: { ids: [variantId] }
                                      });

                                    undoStateDispatch({
                                      type: UNDO_SHOW,
                                      product: {
                                        variantId,
                                        productId,
                                        title:
                                          lineItem.node.merchandise.product
                                            .title,
                                        quantity: lineItem.node.quantity,
                                        sku: lineItem.node.merchandise.sku,
                                        pimData: error
                                          ? undefined
                                          : formatPimData(rawPimData)
                                      }
                                    });
                                  }
                                });
                              }}
                              qbsMessage={lineItem.node?.qbsDiscountMessage}
                            />
                          </Styled.ProductVariantWrapper>
                        </CSSTransition>
                      );
                    })}
                  </TransitionGroup>
                </Styled.CartLineItems>
                {cart.lines.edges.length > 0 && (
                  <RebuyUpsellWithData
                    dataLabel="miniCart_upsell"
                    cartSubtotal={cart?.cost?.subtotalAmount?.amount}
                    productIds={cartItems.map((item) =>
                      convertGlobalIdToRawId(item.node.merchandise.product.id)
                    )}
                    dataSourceId={REBUY_CART_UPSELL_ID}
                  />
                )}
              </>
            )}
          </Styled.ScrollContainer>

          <Styled.CartFooter
            showViewBagLink
            isGiftingPrioritized={isGiftingPrioritized}
            onRequestClose={onRequestClose}
            currencyCode={cart?.cost?.subtotalAmount?.currencyCode}
            subtotalPrice={cart?.cost?.subtotalAmount?.amount}
            totalItems={cart?.lines?.edges.length}
            hasGiftableProduct={cart?.lines?.edges.some(
              (item) =>
                !item.node.merchandise.product.handle.includes('gift-card')
            )}
            discountCode={discountObject?.code}
            discountAmount={codeDiscountAmount}
            discountPercentage={codeDiscountPercentage}
            checkoutUrl={patchCartUrl(cart?.checkoutUrl)}
            onCheckoutClick={(href) => {
              hive.track('MINICART_CHECKOUT_CLICK');

              if (isLoopOnStore()) {
                const variantIds = [];
                cart.lines.edges.forEach((lineItem) => {
                  for (let i = 0; i < lineItem.node.quantity; i += 1) {
                    variantIds.push(
                      convertGlobalIdToRawId(lineItem.node.merchandise.id)
                    );
                  }
                });
                beginLoopCheckout(variantIds);
              } else {
                window.location = href;
              }
            }}
            giftMessage={cart?.note}
            onGiftOptionsClick={() => setDialogToDisplay(GIFT_OPTIONS)}
            siteConfig={siteConfig}
            setViewCouponDialogDisplay={setViewCouponDialogDisplay}
          />
        </Styled.CartDialog>

        <GiftOptionsDialog
          isOpen={dialogToDisplay === GIFT_OPTIONS}
          upsellCollectionId={upsellCollectionId}
          upsellTitle={upsellTitle}
          upsellSubtitle={upsellSubtitle}
          note={cart?.note}
          onCloseClick={() => setDialogToDisplay(null)}
        />

        <GiftMessageDialog
          isOpen={dialogToDisplay === GIFT_MESSAGE}
          note={cart?.note}
          onCloseClick={() => setDialogToDisplay(null)}
        />
      </>
    );
  }
);

CartDialog.propTypes = {
  /**
   * object representing a cart, retrieved from storefront
   */
  cart: CartPropTypes.cart,
  /**
   * amount required before shipping is free
   */
  freeShippingThreshold: PropTypes.number,
  /**
   * Shipping method available for free shipping
   */
  freeShippingMethod: PropTypes.string,
  /**
   * Parent component maintains the open/close state and passes down
   */
  isOpen: PropTypes.bool.isRequired,
  /**
   * A function that is called to add a product to the cart.
   */
  onAddToCart: PropTypes.func.isRequired,
  /**
   * A function that is called when `CartDialog` is requesting to close.
   */
  onRequestClose: PropTypes.func.isRequired,
  /**
   * A function that is called when a ProductVariant wants to update its quantity.
   * @param {String} lineItemId
   * @param {Number} quantity
   * @param {Number} variantId
   */
  onQuantityUpdate: PropTypes.func.isRequired,
  /**
   * Collection id for the list of gift option upsells to display
   */
  upsellCollectionId: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    .isRequired,
  /**
   * Title for the gift option upsell collection
   */
  upsellTitle: PropTypes.string.isRequired,
  /**
   * Subtitle for the gift option upsell collection
   */
  upsellSubtitle: PropTypes.string.isRequired,
  /**
   * shipping and return policy from contentful
   */
  shippingPolicy: PropTypes.shape({
    cta: PropTypes.string,
    display: PropTypes.bool,
    title: PropTypes.string,
    sections: PropTypes.arrayOf(PropTypes.shape({})),
    defaultSection: PropTypes.shape({})
  }),
  /**
   * `true` if coupon code is auto-applied site-wide
   */
  isAutoApplied: PropTypes.bool,
  /**
   * Discount code percentage set in Contentful, should match percentage value set for that coupon code in Shopify
   */
  codeDiscountPercentage: PropTypes.number,
  /**
   * Site config object from Contentful
   */
  siteConfig: PropTypes.shape({
    coupon_code: PropTypes.shape({
      value: PropTypes.string
    }),
    is_discount_auto_applied: PropTypes.shape({
      value: PropTypes.bool
    }),
    sms_terms_of_service: PropTypes.shape({
      value: PropTypes.shape({})
    }),
    coupon_code_percentage: PropTypes.shape({
      value: PropTypes.string
    })
  }),
  /**
   * Function to set the view to display for the coupon dialog
   */
  setViewCouponDialogDisplay: PropTypes.func.isRequired
};

CartDialog.defaultProps = {
  cart: null,
  freeShippingThreshold: 50,
  freeShippingMethod: 'US',
  shippingPolicy: {},
  isAutoApplied: false,
  codeDiscountPercentage: 20,
  siteConfig: undefined
};

export default CartDialog;
